import React, { useEffect, useMemo } from 'react';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import {
	whoCanBrowsePrompt,
	whoCanCreateWorkItemsPrompt,
	whoHasAdministerSpacesPermissionPrompt,
} from '@jira/ai-rovo__actions-permissions/src/SpacePermissionConfigProviderEntry.tsx';
import { useExplicitPermissionsSkillPrompt } from '@jira/ai-rovo__actions-permissions/src/useExplicitPermissionsSkillPrompt.tsx';
import { useAiRovoCurrentRouteConfigurationStoreActions } from '@jira/ai-rovo_actions-store/src/aiRovoCurrentRouteConfigurationStore.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	HandleActionType,
	type ConversationAction,
} from '@atlassian/rovo-conversation-actions/types';
import { useRandomNudge } from './useRandomNudge.tsx';

export const AiRovoSpacePermissionNudgesProvider: React.FC<{}> = (): null => {
	const { formatMessage } = useIntl();
	const { updateConfiguration } = useAiRovoCurrentRouteConfigurationStoreActions();
	const { formatExplicitPermissionsSkillPrompt } = useExplicitPermissionsSkillPrompt();

	const nudges = useMemo<ConversationAction[]>(
		() => [
			{
				id: 'ai-rovo.space-permissions-nudge.who-can-browse',
				title: formatMessage(whoCanBrowsePrompt),
				description: formatMessage(whoCanBrowsePrompt),
				icon: () => <AiChatIcon label={formatMessage(whoCanBrowsePrompt)} size="medium" />,
				type: 'skill-action',
				handleType: HandleActionType.Skill,
				staticPrompt: formatExplicitPermissionsSkillPrompt(whoCanBrowsePrompt),
			},
			{
				id: 'ai-rovo.space-permissions-nudge.who-can-create-work-items',
				title: formatMessage(whoCanCreateWorkItemsPrompt),
				description: formatMessage(whoCanCreateWorkItemsPrompt),
				icon: () => <AiChatIcon label={formatMessage(whoCanCreateWorkItemsPrompt)} size="medium" />,
				type: 'skill-action',
				handleType: HandleActionType.Skill,
				staticPrompt: formatExplicitPermissionsSkillPrompt(whoCanCreateWorkItemsPrompt),
			},
			{
				id: 'ai-rovo.space-permissions-nudge.who-has-administer-spaces-permission',
				title: formatMessage(whoHasAdministerSpacesPermissionPrompt),
				description: formatMessage(whoHasAdministerSpacesPermissionPrompt),
				icon: () => (
					<AiChatIcon label={formatMessage(whoHasAdministerSpacesPermissionPrompt)} size="medium" />
				),
				type: 'skill-action',
				handleType: HandleActionType.Skill,
				staticPrompt: formatExplicitPermissionsSkillPrompt(whoHasAdministerSpacesPermissionPrompt),
			},
		],
		[formatMessage, formatExplicitPermissionsSkillPrompt],
	);

	const randomNudge = useRandomNudge(nudges);

	useEffect(() => {
		if (randomNudge) {
			updateConfiguration({ nudges: [randomNudge] });
		}
	}, [randomNudge, updateConfiguration]);

	return null;
};
